import * as RadixAccordion from '@radix-ui/react-accordion';
import classNames from 'classnames';
import React, { ReactNode } from 'react';
import { notNil } from '../../core/helpers/is-nil';
import { Icon } from '../icon/icon';
import { PictoVariant } from '../icon/icon-data';
import { Typography } from '../typography/typography';
import styles from './accordion.module.scss';

export type AccordionItem = {
  value: string;
  triggerText: string;
  content: ReactNode;
  picto?: PictoVariant;
};

type Props = {
  title?: string;
  items: AccordionItem[];
};

export const Accordion: React.FC<Props> = ({ title, items }) => {
  const hasTitle = notNil(title);
  return (
    <div className={styles.root}>
      {hasTitle ? <Typography variant="headline-3">{title}</Typography> : null}
      <RadixAccordion.Root type="multiple">
        {items.map(({ value, triggerText, content, picto }) => (
          <RadixAccordion.Item key={value} className={styles.item} value={value}>
            <AccordionTrigger picto={picto}>{triggerText}</AccordionTrigger>
            <AccordionContent hasIcon={notNil(picto)}>{content}</AccordionContent>
          </RadixAccordion.Item>
        ))}
      </RadixAccordion.Root>
    </div>
  );
};

type AccordionTriggerProps = Pick<AccordionItem, 'picto'>;

const AccordionTrigger: React.FC<RadixAccordion.AccordionTriggerProps & AccordionTriggerProps> = ({
  children,
  className,
  picto,
  ...props
}) => {
  const hasIcon = notNil(picto);
  return (
    <RadixAccordion.Header>
      <RadixAccordion.Trigger className={styles.trigger} {...props}>
        {hasIcon ? <Icon className={styles.icon} size="32" variant={picto} /> : null}
        <Typography className={styles.text} variant="headline-3">
          {children}
        </Typography>
        <Icon aria-hidden className={classNames(styles.icon, styles.plus)} size="24" variant="plus" />
        <Icon aria-hidden className={classNames(styles.icon, styles.minus)} size="24" variant="minus" />
      </RadixAccordion.Trigger>
    </RadixAccordion.Header>
  );
};

const AccordionContent: React.FC<RadixAccordion.AccordionContentProps & { hasIcon: boolean }> = ({
  children,
  className,
  hasIcon,
  ...props
}) => (
  <RadixAccordion.Content {...props} className={classNames(styles.content, { [styles.bigLeftPadding]: hasIcon })}>
    {children}
  </RadixAccordion.Content>
);
